import { useEffect, useMemo, useState } from 'react';
import {
  GridColDef,
  GridRenderCellParams,
  gridClasses,
  GridFilterItem,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
  GridRowClassNameParams,
  GridValueGetter,
} from '@mui/x-data-grid-premium';
import { styled, useTheme } from '@mui/material/styles';
import RawTableContainer from '@mui/material/TableContainer';
import {
  ContractLegalStatus,
  ContractsByMissionGQL,
  TemplateContract,
  LegalContractGQL,
  MissionDataForGetLegalContractsResult,
  Role,
} from '@bloomays-lib/types.shared';
import { FileViewer, LoaderPending, Tag, Modal, HrefLink, InformationTip } from '@bloomays-lib/ui.shared';
import RawDataGrid from '../molecules/DataGrid';
import IconButton from '@mui/material/IconButton';
import isEmpty from 'lodash/fp/isEmpty';
import values from 'lodash/fp/values';
import map from 'lodash/fp/map';
import compact from 'lodash/fp/compact';
import pipe from 'lodash/fp/pipe';
import flatten from 'lodash/fp/flatten';
import uniq from 'lodash/fp/uniq';
import sortBy from 'lodash/fp/sortBy';
import flow from 'lodash/fp/flow';
import { useTranslation } from 'react-i18next';
import LegalContractFile from '../molecules/LegalContractFile';
import { useAuthStore } from '../../stores';

type TagContractTypeProps = {
  type: TemplateContract;
};

export type LegalContractRowData = LegalContractGQL &
  Omit<MissionDataForGetLegalContractsResult, 'id' | 'label'> & {
    missionId: string;
    missionLabel: string;
  };

const getAccountManagerName = (accountManager?: MissionDataForGetLegalContractsResult['accountManager']): string => {
  const firstname = accountManager?.firstname ?? '';
  const lastname = accountManager?.lastname ?? '';
  return `${firstname} ${lastname}`.trim();
};

const parseContractsByMissionGQL = (missionsContracts: ContractsByMissionGQL[]): LegalContractRowData[] => {
  const temp = map((contractByMission) => {
    const { mission, contracts } = contractByMission;
    const { bloomerId, bloomerFullName, clientSocietyId, clientSocietyName, bloomerSocietyName, accountManager } =
      mission;

    const rows: LegalContractRowData[] = map((contract) => {
      return {
        ...contract,
        bloomerId,
        bloomerFullName,
        clientSocietyId,
        clientSocietyName,
        bloomerSocietyName,
        accountManager,
        missionId: mission.id,
        missionLabel: mission.label,
      };
    }, contracts);
    return rows;
  }, missionsContracts);

  return pipe(flatten, compact)(temp);
};

const TagContractType = ({ type }: TagContractTypeProps) => {
  const theme = useTheme();
  return (
    <Tag
      text={type}
      aria-label={type}
      backgroundColor={type === TemplateContract.SST ? theme.palette.blue[60] : theme.palette.red[60]}
      color={theme.palette.common.white}
    />
  );
};

export type TableLegalContractsType = {
  missionsContracts: ContractsByMissionGQL[];
};

const TableLegalContracts = ({ missionsContracts }: TableLegalContractsType) => {
  const { t } = useTranslation(['legalContracts', 'mission']);
  const apiRef = useGridApiRef();
  const [token, setToken] = useState<string | undefined>();
  const auth = useAuthStore((state) => state.auth);
  const groupedByMission = missionsContracts.length !== 1;

  useEffect(() => {
    if (auth?.getAccessTokenSilently) {
      auth.getAccessTokenSilently().then((token) => {
        setToken(token);
      });
    }
  }, [auth]);

  const [fileViewerPath, setFileViewerPath] = useState<string | undefined>(undefined);
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);
  const legalContracts: LegalContractRowData[] = useMemo(
    () => parseContractsByMissionGQL(missionsContracts),
    [missionsContracts],
  );

  const accountManagerOptions = useMemo(() => {
    return flow(
      map((legalContract: LegalContractRowData) => getAccountManagerName(legalContract.accountManager)),
      compact,
      uniq,
      sortBy((accountManagerName) => accountManagerName.toLocaleLowerCase()),
    )(legalContracts);
  }, [legalContracts]);

  const isAdmin = auth?.user?.roles?.includes(Role.Admin) || false;
  const isSystem = auth?.user?.roles?.includes(Role.System) || false;
  const shouldApplyDefaultFilter = isAdmin && !isSystem;

  const defaultAccountManagerName = useMemo(() => {
    if (!shouldApplyDefaultFilter) return undefined;
    const displayName = auth?.user?.displayName;

    return displayName || undefined;
  }, [auth?.user?.displayName, shouldApplyDefaultFilter]);

  useEffect(() => {
    if (!groupedByMission) return;

    const existingFilterItems = apiRef.current?.state?.filter?.filterModel?.items;
    const hasAnyFilter = !!existingFilterItems?.some((item: GridFilterItem) => !!item?.value);
    if (hasAnyFilter) return;

    const defaultItems: GridFilterItem[] = [
      {
        field: 'status',
        operator: 'isAnyOf',
        value: ['refused', 'on going', 'pending'] satisfies ContractLegalStatus[],
      },
    ];

    if (defaultAccountManagerName) {
      defaultItems.push({
        field: 'accountManagerName',
        operator: 'is',
        value: defaultAccountManagerName,
      });
    }

    apiRef.current?.setFilterModel({ items: defaultItems });
  }, [apiRef, defaultAccountManagerName, groupedByMission]);

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      sorting: {
        sortModel: [{ field: 'createdAt', sort: 'asc' }],
      },
      rowGrouping: groupedByMission
        ? {
            model: ['missionLabel'],
          }
        : undefined,
    },
  });

  const contractStatus: Record<ContractLegalStatus, { value: ContractLegalStatus; label: string }> = {
    pending: { value: 'pending', label: t('pending') },
    'on going': { value: 'on going', label: t('onGoing') },
    done: { value: 'done', label: t('done') },
    cancelled: { value: 'cancelled', label: t('cancelled') },
    refused: { value: 'refused', label: t('refused') },
  };
  const columns: GridColDef<LegalContractRowData>[] = [
    {
      field: 'step',
      headerName: t('step'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      renderCell: (params: GridRenderCellParams<LegalContractRowData>) => {
        return (
          <>
            {params.row.step} <InformationTip>{params.row.recordId}</InformationTip>
          </>
        );
      },
    },
    {
      field: 'type',
      headerName: t('type'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      type: 'singleSelect',
      valueOptions: [TemplateContract.SST, TemplateContract.PDS],
      renderCell: (params) => {
        if (params.rowNode.type === 'group') {
          if (params.field !== '__row_group_by_columns_group__') return '';
          const groupingKey = params.rowNode.groupingKey as TemplateContract;
          if (!groupingKey) return;
          return <TagContractType type={groupingKey} />;
        }

        if (params.row.type) {
          return <TagContractType type={params.row.type} />;
        }
      },
    },
    {
      field: 'status',
      headerName: t('status'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      type: 'singleSelect',
      valueOptions: values(contractStatus),
      renderCell: (params: GridRenderCellParams<LegalContractRowData>) => {
        if (params.rowNode.type === 'group') {
          if (params.field !== '__row_group_by_columns_group__') return '';
          const groupingKey = params.rowNode.groupingKey as ContractLegalStatus;
          if (!groupingKey) return;
          const t = contractStatus[groupingKey];
          return (
            <IconButton size="small" title={t.label}>
              {t.label}
            </IconButton>
          );
        }

        if (params.row.status) {
          const t = contractStatus[params.row.status];
          return (
            <IconButton size="small" title={t.label}>
              {t.label}
            </IconButton>
          );
        }
      },
    },
    {
      field: 'createdAt',
      headerName: t('createdAt'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      type: 'date',
      valueGetter: (value: GridValueGetter<LegalContractRowData>, row: LegalContractRowData): Date | undefined => {
        return row.createdAt ? new Date(row.createdAt) : undefined;
      },
    },
    {
      field: 'signedDate',
      headerName: t('signedDate'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      type: 'date',
      valueGetter: (value: GridValueGetter<LegalContractRowData>, row: LegalContractRowData): Date | undefined => {
        return row.signedDate ? new Date(row.signedDate) : undefined;
      },
    },
    {
      field: 'emptyFileUrl',
      headerName: t('emptyFileUrl'),
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      renderCell: (params: GridRenderCellParams<LegalContractRowData>) => {
        if (params.rowNode.type === 'group') {
          return '';
        }
        if (params.row.status === 'pending') {
          return '⚠️';
        }
        return (
          <LegalContractFile
            setUploadingFile={setUploadingFile}
            legalContract={params.row}
            signed={false}
            missionRecordId={params.row.missionId}
            createInvitation={true}
            onClickPreview={setFileViewerPath}
          />
        );
      },
    },
    {
      field: 'signedFileUrl',
      headerName: t('signedFileUrl'),
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      renderCell: (params: GridRenderCellParams<LegalContractRowData>) => {
        if (params.rowNode.type === 'group') {
          return '';
        }
        if (params.row.status === 'pending') {
          return '⚠️';
        }
        return (
          <LegalContractFile
            setUploadingFile={setUploadingFile}
            legalContract={params.row}
            signed={true}
            missionRecordId={params.row.missionId}
            createInvitation={false}
            onClickPreview={setFileViewerPath}
          />
        );
      },
    },
    {
      field: 'refusedReason',
      headerName: t('refusedReason'),
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      renderCell: (params: GridRenderCellParams<LegalContractRowData>) => {
        return params.row.refusedReason ? <InformationTip>{params.row.refusedReason}</InformationTip> : '';
      },
    },
    {
      field: 'signLink',
      headerName: t('signLink'),
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      renderCell: (params: GridRenderCellParams<LegalContractRowData>) => {
        return (
          <HrefLink href={params.row.signLink ?? ''} target="_blank">
            {params.row.signLink}
          </HrefLink>
        );
      },
    },
  ];

  if (groupedByMission) {
    columns.unshift({
      field: 'accountManagerName',
      headerName: t('accountManager', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      type: 'singleSelect',
      valueOptions: accountManagerOptions,
      valueGetter: (_value: GridValueGetter<LegalContractRowData>, row: LegalContractRowData): string => {
        return getAccountManagerName(row.accountManager);
      },
    });
    columns.unshift({
      field: 'clientSocietyName',
      headerName: t('clientSocietyName', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    });
    columns.unshift({
      field: 'bloomerSocietyName',
      headerName: t('bloomerSocietyName', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    });
    columns.unshift({
      field: 'bloomerFullName',
      headerName: t('bloomerFullName', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    });
    columns.unshift({
      field: 'missionLabel',
      headerName: t('label', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    });
  }

  return (
    <>
      {uploadingFile && <LoaderPending text={t('uploading')} />}
      {!uploadingFile && isEmpty(legalContracts) && <>{t('noContractFounds')}</>}
      <Modal
        open={!!fileViewerPath}
        onClose={() => setFileViewerPath(undefined)}
        additionnalCSS={{
          width: '1250px',
          padding: '32px',
          border: 'none',
          borderRadius: '20px',
          height: '98%',
        }}
      >
        <FileViewer width="1200px" url={fileViewerPath as string} accessToken={token} mimeType="application/pdf" />
      </Modal>
      <TableContainer>
        <DataGrid
          apiRef={apiRef}
          uniqueDatagridId={groupedByMission ? 'table-contracts-global-v1' : 'table-contracts-mission-v1'}
          defaultGroupingExpansionDepth={-1}
          initialState={initialState}
          groupingColDef={groupedByMission ? { filterable: false } : undefined}
          getTogglableColumns={
            groupedByMission
              ? (columns: GridColDef[]) =>
                  columns.filter((col: GridColDef) => col.field !== 'missionLabel').map((col: GridColDef) => col.field)
              : undefined
          }
          getRowClassName={(params: GridRowClassNameParams) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          hideFooterPagination={false}
          sortingMode="client"
          filterMode="client"
          rows={legalContracts}
          getRowId={(row: LegalContractGQL) => row.recordId as string}
          columns={columns}
          pageSize={20}
          pagination={true}
          sx={{
            width: '100%',
          }}
        />
      </TableContainer>
    </>
  );
};

export default TableLegalContracts;

const DataGrid = styled(RawDataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.neutral[10],
  },
}));

const TableContainer = styled(RawTableContainer)(() => ({
  margin: '0 auto',
}));
